import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ShieldCheck, Plus, AlertTriangle, Clock, CheckCircle, XCircle } from 'lucide-react'
import { api } from '../lib/api'

const C = {
  bg: 'var(--color-bg)', bgCard: 'var(--color-bg-card)', border: 'var(--color-border)',
  amber: 'var(--color-primary)', green: '#2D6A2F', text: 'var(--color-text)',
  muted: 'var(--color-text-muted)', faint: 'var(--color-text-faint)', danger: '#F87171',
  hover: 'var(--color-bg-hover)',
}

interface Permit {
  id: string
  permit_type: string
  permit_number: string
  issuing_authority: string
  issue_date: string
  expiry_date: string | null
  status: 'active' | 'expired' | 'pending' | 'revoked'
  notes: string
  document_url: string | null
  created_at: string
}

function daysUntil(dateStr: string | null): number | null {
  if (!dateStr) return null
  const diff = new Date(dateStr).getTime() - Date.now()
  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

function urgencyColor(days: number | null): string {
  if (days === null) return C.muted
  if (days < 0) return C.danger
  if (days <= 7) return '#F87171'
  if (days <= 30) return C.amber
  return '#4ADE80'
}

function statusIcon(status: string, days: number | null) {
  if (status === 'expired' || (days !== null && days < 0)) return <XCircle size={14} color={C.danger} />
  if (status === 'pending') return <Clock size={14} color={C.amber} />
  if (status === 'revoked') return <XCircle size={14} color={C.danger} />
  if (days !== null && days <= 30) return <AlertTriangle size={14} color={C.amber} />
  return <CheckCircle size={14} color="#4ADE80" />
}

const PERMIT_TYPES = [
  'Nursery License', 'Retail Nursery License', 'Pesticide Applicator',
  'Water Use Permit', 'Organic Certification', 'Farm Bureau Membership',
  'Business License', 'Sales Tax Certificate', 'Insurance Policy',
  'Environmental Permit', 'Irrigation Permit', 'Other',
]

export function PermitsPage() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({
    permit_type: '', permit_number: '', issuing_authority: '',
    issue_date: '', expiry_date: '', status: 'active', notes: '',
  })

  const { data: permits = [], isLoading } = useQuery<Permit[]>({
    queryKey: ['permits'],
    queryFn: () => api.get('/permits').then(r => r.data),
  })

  const create = useMutation({
    mutationFn: (body: typeof form) => api.post('/permits', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['permits'] }); setShowForm(false); setForm({ permit_type: '', permit_number: '', issuing_authority: '', issue_date: '', expiry_date: '', status: 'active', notes: '' }) },
  })

  // Sort: expired/expiring soon first, then by expiry date
  const sorted = [...permits].sort((a, b) => {
    const dA = daysUntil(a.expiry_date) ?? 9999
    const dB = daysUntil(b.expiry_date) ?? 9999
    return dA - dB
  })

  const active = sorted.filter(p => p.status === 'active' && (daysUntil(p.expiry_date) ?? 1) > 0)
  const expiring = sorted.filter(p => p.status === 'active' && daysUntil(p.expiry_date) !== null && (daysUntil(p.expiry_date) as number) <= 30 && (daysUntil(p.expiry_date) as number) >= 0)
  const expired = sorted.filter(p => p.status === 'expired' || (daysUntil(p.expiry_date) !== null && (daysUntil(p.expiry_date) as number) < 0))

  const label: React.CSSProperties = { fontSize: 11, color: C.muted, marginBottom: 4, display: 'block' }
  const input: React.CSSProperties = { width: '100%', background: 'var(--color-bg-hover)', border: `1px solid ${C.border}`, borderRadius: 6, padding: '7px 10px', color: C.text, fontSize: 13 }
  const btn = (variant: 'primary' | 'ghost'): React.CSSProperties => ({
    padding: '8px 14px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500,
    background: variant === 'primary' ? C.amber : 'transparent',
    color: variant === 'primary' ? 'var(--color-bg)' : C.muted,
  })

  function PermitCard({ permit }: { permit: Permit }) {
    const days = daysUntil(permit.expiry_date)
    return (
      <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              {statusIcon(permit.status, days)}
              <span style={{ fontWeight: 600, fontSize: 14, color: C.text }}>{permit.permit_type}</span>
            </div>
            {permit.permit_number && (
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 2 }}>#{permit.permit_number}</div>
            )}
            {permit.issuing_authority && (
              <div style={{ fontSize: 12, color: C.faint }}>{permit.issuing_authority}</div>
            )}
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            {permit.expiry_date && (
              <>
                <div style={{ fontSize: 12, color: urgencyColor(days), fontWeight: 600 }}>
                  {days !== null && days < 0 ? 'Expired' : days !== null && days === 0 ? 'Expires today' : days !== null ? `${days}d left` : ''}
                </div>
                <div style={{ fontSize: 11, color: C.faint }}>
                  Expires {new Date(permit.expiry_date).toLocaleDateString()}
                </div>
              </>
            )}
            {!permit.expiry_date && <div style={{ fontSize: 12, color: C.muted }}>No expiry</div>}
          </div>
        </div>
        {permit.notes && (
          <div style={{ marginTop: 10, fontSize: 12, color: C.muted, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
            {permit.notes}
          </div>
        )}
      </div>
    )
  }

  return (
    <main id="main-content" style={{ padding: '28px 32px', maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>Permits & Licenses</h1>
          <p style={{ fontSize: 13, color: C.muted, margin: '4px 0 0' }}>
            Track nursery licenses, certifications, insurance policies, and regulatory permits
          </p>
        </div>
        <button style={btn('primary')} onClick={() => setShowForm(true)}>
          <Plus size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
          Add Permit
        </button>
      </div>

      {/* Summary chips */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
        {[
          { label: 'Active', count: active.length - expiring.length, color: '#4ADE80' },
          { label: 'Expiring soon', count: expiring.length, color: C.amber },
          { label: 'Expired', count: expired.length, color: C.danger },
        ].map(({ label: l, count, color }) => (
          <div key={l} style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShieldCheck size={14} color={color} />
            <span style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{count}</span>
            <span style={{ fontSize: 12, color: C.muted }}>{l}</span>
          </div>
        ))}
      </div>

      {/* Add form */}
      {showForm && (
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 24px', marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: C.text }}>New Permit / License</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={label}>Permit Type</label>
              <select style={input} value={form.permit_type} onChange={e => setForm(f => ({ ...f, permit_type: e.target.value }))}>
                <option value="">Select type…</option>
                {PERMIT_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={label}>Permit / License Number</label>
              <input style={input} placeholder="e.g. NL-2024-001234" value={form.permit_number} onChange={e => setForm(f => ({ ...f, permit_number: e.target.value }))} />
            </div>
            <div>
              <label style={label}>Issuing Authority</label>
              <input style={input} placeholder="e.g. Florida Dept of Agriculture" value={form.issuing_authority} onChange={e => setForm(f => ({ ...f, issuing_authority: e.target.value }))} />
            </div>
            <div>
              <label style={label}>Status</label>
              <select style={input} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="expired">Expired</option>
                <option value="revoked">Revoked</option>
              </select>
            </div>
            <div>
              <label style={label}>Issue Date</label>
              <input type="date" style={input} value={form.issue_date} onChange={e => setForm(f => ({ ...f, issue_date: e.target.value }))} />
            </div>
            <div>
              <label style={label}>Expiry Date (leave blank if no expiry)</label>
              <input type="date" style={input} value={form.expiry_date} onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))} />
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={label}>Notes</label>
            <textarea style={{ ...input, minHeight: 64, resize: 'vertical' }} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btn('primary')} onClick={() => create.mutate(form)} disabled={!form.permit_type}>
              {create.isPending ? 'Saving…' : 'Save Permit'}
            </button>
            <button style={btn('ghost')} onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {isLoading && <p style={{ color: C.muted, textAlign: 'center' }}>Loading permits…</p>}

      {!isLoading && permits.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: C.muted }}>
          <ShieldCheck size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
          <p>No permits yet. Add your nursery license, organic cert, or insurance policy.</p>
        </div>
      )}

      {/* Expiring soon alert */}
      {expiring.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            <AlertTriangle size={14} color={C.amber} />
            <span style={{ fontSize: 12, fontWeight: 600, color: C.amber, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Expiring Within 30 Days</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {expiring.map(p => <PermitCard key={p.id} permit={p} />)}
          </div>
        </div>
      )}

      {/* All permits */}
      {sorted.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {sorted.filter(p => !expiring.includes(p)).map(p => <PermitCard key={p.id} permit={p} />)}
        </div>
      )}
    </main>
  )
}
